import i18n from '@/utils/i18n';
import React, { useRef, useState } from 'react';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { Animated, Pressable, View } from 'react-native';
import { ClearAllConfirmationModal } from '@/experiences/sales-portal/domain/insights/components/clear-all-confirmation-modal';
import { createStyles } from '@/contexts/theme-context';
import { HiddenAlertsList } from '@/experiences/sales-portal/domain/insights/components/hidden-alerts-list';
import { Icons, Typography, XStack, YStack } from '@usepepper/sprout-kit-ui';
import { useAlertPress } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-alert-press';

const CHEVRON_SPIN_DURATION_MS = 150;
const HEADER_HEIGHT = 56;
const TITLE_ENTER_STYLE = { opacity: 0 };

export interface HiddenAlertsSectionProps {
  alerts: Alert[];
  clearing: boolean;
  onClearAll: () => void;
  onDelete: (alert: Alert) => void;
  onRestore: (alert: Alert) => void;
  supplierUUID: string;
}

export const HiddenAlertsSection: React.FC<HiddenAlertsSectionProps> = ({
  alerts,
  clearing,
  onClearAll,
  onDelete,
  onRestore,
  supplierUUID,
}) => {
  const [styles, theme] = useStyles();
  const { onAccountDetailPress, onAlertPress } = useAlertPress(supplierUUID);
  const [expanded, setExpanded] = useState(false);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const chevronSpin = useRef(new Animated.Value(0)).current;

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    Animated.timing(chevronSpin, {
      duration: CHEVRON_SPIN_DURATION_MS,
      toValue: next ? 1 : 0,
      useNativeDriver: true,
    }).start();
  };

  const chevronRotation = chevronSpin.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  return (
    <>
      <View style={[styles.container, expanded ? styles.boxExpanded : null]}>
        <Pressable
          accessibilityLabel={i18n.t('insights_hub.hidden_alerts.title')}
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          onPress={toggleExpanded}
        >
          <XStack
            alignItems="center"
            gap={theme.sk.spacing.spaceSmall}
            justifyContent="space-between"
            paddingBottom={expanded ? 0 : '$12'}
            paddingHorizontal="$16"
            paddingTop="$12"
            {...(expanded ? {} : { height: HEADER_HEIGHT })}
          >
            <YStack>
              <Typography
                animation="quick"
                enterStyle={TITLE_ENTER_STYLE}
                key={expanded ? 'expanded' : 'collapsed'}
                size={expanded ? '$lg' : '$sm'}
                textAlign="left"
                variant="label"
              >
                {i18n.t('insights_hub.hidden_alerts.title')}
              </Typography>
              {!expanded ? (
                <Typography muted size="$xs">
                  {i18n.t('insights_hub.hidden_alerts_section')}
                </Typography>
              ) : null}
            </YStack>

            <XStack alignItems="center" gap={theme.sk.spacing.spaceMedium}>
              {expanded ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => setConfirmClearOpen(true)}
                >
                  <Typography
                    bold
                    color={theme.sk.color.text.primary}
                    size="$sm"
                  >
                    {i18n.t('insights_hub.hidden_alerts.clear_all')}
                  </Typography>
                </Pressable>
              ) : (
                <Typography
                  color={theme.sk.color.text.tertiary}
                  size="$sm"
                  variant="label"
                >
                  {alerts.length}
                </Typography>
              )}

              {/* Rotation goes on a wrapping view: a transform on the svg
                  itself is dropped by react-native-web. */}
              <Animated.View
                style={{ transform: [{ rotate: chevronRotation }] }}
              >
                <Icons.ChevronDown
                  color={theme.sk.color.icon.primary}
                  size="$24"
                />
              </Animated.View>
            </XStack>
          </XStack>
        </Pressable>

        {expanded ? (
          <HiddenAlertsList
            alerts={alerts}
            onAccountDetailPress={onAccountDetailPress}
            onAlertPress={onAlertPress}
            onDelete={onDelete}
            onRestore={onRestore}
          />
        ) : null}
      </View>

      <ClearAllConfirmationModal
        isOpen={confirmClearOpen}
        loading={clearing}
        onCancel={() => setConfirmClearOpen(false)}
        onConfirm={() => {
          setConfirmClearOpen(false);
          onClearAll();
        }}
      />
    </>
  );
};

const useStyles = createStyles((theme) => ({
  boxExpanded: {
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.radii.radiusLarge,
    borderWidth: 1,
    marginBottom: theme.sk.spacing.spaceXlarge,
    overflow: 'hidden',
  },
}));
